<template>
  <div class="form-block indexing">
    <div class="block-head">
      <span class="field-label">{{ $t("indexing.title") }}</span>
      <span
        class="badge-soft"
        :class="robotsIndex ? 'badge-green' : 'badge-yellow'"
      >
        {{ robotsIndex ? $t("indexing.indexed") : $t("indexing.noindex") }}
      </span>
    </div>

    <div class="indexing-grid">
      <div class="col-settings">
        <div
          class="vis-cards"
          role="radiogroup"
          :aria-label="$t('indexing.visibility')"
        >
          <button
            type="button"
            role="radio"
            class="vis-card"
            :class="{ on: robotsIndex }"
            :aria-checked="robotsIndex"
            v-on:click="$emit('update:robotsIndex', true)"
          >
            <svg
              viewBox="0 0 24 24"
              width="22"
              height="22"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.3-4.3" />
            </svg>
            <span class="vis-title">{{ $t("indexing.visible") }}</span>
            <span class="hint-text">{{ $t("indexing.visibleHint") }}</span>
          </button>
          <button
            type="button"
            role="radio"
            class="vis-card warn"
            :class="{ on: !robotsIndex }"
            :aria-checked="!robotsIndex"
            v-on:click="$emit('update:robotsIndex', false)"
          >
            <svg
              viewBox="0 0 24 24"
              width="22"
              height="22"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path
                d="M17.9 17.9A10.9 10.9 0 0112 20C5 20 1 12 1 12a18.5 18.5 0 015.1-5.9M9.9 4.2A9.1 9.1 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.2 3.2M1 1l22 22"
              />
            </svg>
            <span class="vis-title">{{ $t("indexing.hidden") }}</span>
            <span class="hint-text">{{ $t("indexing.hiddenHint") }}</span>
          </button>
        </div>
        <span class="hint-text warn-text" v-if="!robotsIndex">
          {{ $t("indexing.robotsWarn") }}
        </span>

        <div class="switch-list" v-if="robotsIndex">
          <div class="switch-row">
            <div class="switch-text">
              <span class="switch-label">{{ $t("indexing.followLinks") }}</span>
              <span class="hint-text">{{
                $t("indexing.followLinksHint")
              }}</span>
            </div>
            <button
              type="button"
              role="switch"
              class="sw"
              :class="{ on: robots.follow !== false }"
              :aria-checked="robots.follow !== false"
              :aria-label="$t('indexing.followLinks')"
              v-on:click="setRobots({ follow: robots.follow === false })"
            >
              <span></span>
            </button>
          </div>
          <div class="switch-row">
            <div class="switch-text">
              <span class="switch-label">{{ $t("indexing.noarchive") }}</span>
              <span class="hint-text">noarchive</span>
            </div>
            <button
              type="button"
              role="switch"
              class="sw"
              :class="{ on: !!robots.noarchive }"
              :aria-checked="!!robots.noarchive"
              :aria-label="$t('indexing.noarchive')"
              v-on:click="setRobots({ noarchive: !robots.noarchive })"
            >
              <span></span>
            </button>
          </div>
          <div class="switch-row column">
            <div class="switch-text">
              <span class="switch-label">{{
                $t("indexing.snippetLength")
              }}</span>
              <span class="hint-text">
                {{ $t("indexing.snippetHint") }}
              </span>
            </div>
            <div
              class="segmented"
              role="radiogroup"
              :aria-label="$t('indexing.snippetLength')"
            >
              <button
                v-for="m in modes"
                :key="m.id"
                type="button"
                role="radio"
                :aria-checked="mode === m.id"
                :class="{ active: mode === m.id }"
                v-on:click="pickMode(m.id)"
              >
                {{ $t(m.label) }}
              </button>
            </div>
            <div class="field custom-row" v-if="mode === 'custom'">
              <input
                type="number"
                min="1"
                aria-label="Characters"
                :value="robots.max_snippet"
                v-on:input="setRobots({ max_snippet: $event.target.value })"
              />
              <span class="hint-text">{{ $t("indexing.characters") }}</span>
            </div>
          </div>
        </div>

        <label class="field">
          <span class="field-label">{{ $t("indexing.canonical") }}</span>
          <input
            type="url"
            :value="canonicalUrl"
            placeholder="https://"
            v-on:input="$emit('update:canonicalUrl', $event.target.value)"
          />
          <span class="hint-text">
            {{ $t("indexing.canonicalHint") }}
          </span>
        </label>
      </div>
      <div class="col-preview">
        <div class="preview-box">
          <div class="preview-item" v-for="p in previews" :key="p.id">
            <span class="preview-label">{{ p.label }}</span>
            <div class="preview-body">
              <template v-if="p.id !== 'facebook'">
                <div class="serp-site" :class="p.id">
                  <span class="serp-icon">V</span>
                  <span class="serp-url">{{ shownUrl }}</span>
                </div>
                <span
                  class="serp-title"
                  :class="[p.id, { out: !robotsIndex }]"
                  >{{ title }}</span
                >
                <span class="serp-desc" v-if="robotsIndex && snippetText">{{
                  snippetText
                }}</span>
                <span
                  class="hint hint-text serp-none"
                  v-else-if="!robotsIndex"
                  >{{ $t("indexing.notShown") }}</span
                >
                <div
                  class="serp-meta"
                  v-if="robotsIndex && p.id === 'bing' && !robots.noarchive"
                >
                  <button
                    v-if="p.id === 'bing' && !robots.noarchive"
                    type="button"
                    class="cached-btn"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="13"
                      height="13"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      <path
                        d="M3 12a9 9 0 109-9 9.8 9.8 0 00-6.7 2.8L3 8M3 3v5h5M12 7v5l3 2"
                      />
                    </svg>
                    {{ $t("indexing.cached") }}
                  </button>
                </div>
              </template>
              <template v-else>
                <div class="fb-card">
                  <div class="fb-image">{{ $t("indexing.coverImage") }}</div>
                  <div class="fb-text">
                    <span class="fb-domain">{{ slugDomain }}</span>
                    <span class="fb-title">{{ title }}</span>
                    <span class="fb-desc">{{ description }}</span>
                  </div>
                </div>
                <span class="hint hint-text">{{ $t("indexing.fbNote") }}</span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "IndexingSettings",
  props: {
    robotsIndex: { type: Boolean, default: true },
    robots: { type: Object, default: () => ({}) },
    canonicalUrl: { type: String, default: "" },
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    slugDomain: { type: String, default: "" },
    slug: { type: String, default: "" },
  },
  emits: ["update:robotsIndex", "update:robots", "update:canonicalUrl"],
  data() {
    return {
      mode: "default",
      modes: [
        { id: "default", label: "indexing.modeDefault" },
        { id: "custom", label: "indexing.modeCustom" },
        { id: "none", label: "indexing.modeNone" },
        { id: "unlimited", label: "indexing.modeUnlimited" },
      ],
    };
  },
  computed: {
    previews() {
      return [
        { id: "google", label: "Google" },
        { id: "bing", label: "Bing" },
        { id: "facebook", label: "Facebook" },
      ];
    },
    siteName() {
      return this.slugDomain.split(".")[0];
    },
    shownUrl() {
      return this.canonicalUrl || this.slugDomain + " › " + this.slug;
    },
    // What the search result shows under the title for the chosen snippet length.
    snippetText() {
      if (this.mode === "none") {
        return "";
      }
      const text = this.description ?? "";
      if (this.mode === "custom" && this.robots.max_snippet !== "") {
        const n = Number(this.robots.max_snippet);
        if (n <= 0) {
          return "";
        }
        if (n < text.length) {
          return text.slice(0, n).trimEnd() + "…";
        }
      }
      return text;
    },
  },
  watch: {
    // The parent resets the settings when another page is opened: follow it.
    robots: {
      immediate: true,
      deep: true,
      handler(robots) {
        if (robots.nosnippet) {
          this.mode = "none";
        } else if (robots.max_snippet !== "" && robots.max_snippet != null) {
          this.mode =
            Number(robots.max_snippet) === -1 ? "unlimited" : "custom";
        } else if (this.mode !== "custom") {
          this.mode = "default";
        }
      },
    },
  },
  methods: {
    setRobots(change) {
      this.$emit("update:robots", { ...this.robots, ...change });
    },
    pickMode(mode) {
      this.mode = mode;
      const max = {
        default: { nosnippet: false, max_snippet: "" },
        custom: { nosnippet: false, max_snippet: 160 },
        none: { nosnippet: true, max_snippet: "" },
        unlimited: { nosnippet: false, max_snippet: -1 },
      }[mode];
      this.setRobots(max);
    },
  },
};
</script>

<style scoped>
.indexing {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
/* settings on the left, the search preview beside them; stacked on a narrow modal */
.indexing-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}
.col-settings,
.col-preview {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}
.col-preview {
  position: sticky;
  top: 0;
}
@media (max-width: 40rem) {
  .indexing-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .col-preview {
    position: static;
  }
}
.block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.625rem;
}
.vis-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.625rem;
}
.vis-card {
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.875rem;
  border-radius: 0.75rem;
  background: #fff;
  border: 2px solid #e2e8f0;
  color: #5b6b78;
  cursor: pointer;
  font: inherit;
}
.vis-card.on {
  background: #f5f3ff;
  border-color: #7c3aed;
  color: #7c3aed;
}
.vis-card.warn.on {
  background: #fff7ed;
  border-color: #ea580c;
  color: #c2410c;
}
.vis-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #121212;
}
.warn-text {
  color: #9a3412;
}
.preview-box {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  overflow: hidden;
}
.preview-item + .preview-item {
  border-top: 1px solid #e2e8f0;
}
.preview-label {
  display: block;
  padding: 0.5rem 0.75rem 0;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #5b6b78;
  text-transform: uppercase;
  letter-spacing: 0.03125rem;
}
.preview-body {
  padding: 0.625rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-height: 6rem;
}
.serp-site {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.serp-icon {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: #f1f3f4;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 700;
  color: #5f6368;
}
.serp-site.bing .serp-icon {
  width: 1rem;
  height: 1rem;
  border-radius: 0.25rem;
  font-size: 0.5rem;
}
.serp-site-text {
  display: flex;
  flex-direction: column;
  font-family: Arial, sans-serif;
}
.serp-name {
  font-size: 0.75rem;
  color: #202124;
}
.serp-url {
  font-size: 0.6875rem;
  color: #4d5156;
}
.serp-title {
  font-family: Arial, sans-serif;
  font-size: 1rem;
  line-height: 1.3;
  color: #1a0dab;
}
.serp-title.bing {
  font-family: "Segoe UI", Arial, sans-serif;
  font-size: 0.9375rem;
  color: #1b0daf;
}
.serp-title.out {
  color: #94a3b8;
  text-decoration: line-through;
}
.serp-desc {
  font-family: Arial, sans-serif;
  font-size: 0.75rem;
  line-height: 1.5;
  color: #4d5156;
}
.serp-none {
  font-style: italic;
}
.serp-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.625rem;
  margin-top: 0.375rem;
}
.cached-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.125rem 0.5rem;
  border-radius: 99px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}
.fb-card {
  border: 1px solid #dadde1;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #f0f2f5;
}
.fb-image {
  height: 4.5rem;
  background: #d8dee6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
}
.fb-text {
  padding: 0.625rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  font-family: Helvetica, Arial, sans-serif;
}
.fb-domain {
  font-size: 0.75rem;
  color: #5b6b78;
  text-transform: uppercase;
}
.fb-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #050505;
}
.fb-desc {
  font-size: 0.75rem;
  color: #5b6b78;
}
.switch-list {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  overflow: hidden;
}
.switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0.875rem;
}
.switch-row + .switch-row {
  border-top: 1px solid #f1f5f9;
}
.switch-row.column {
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
}
.switch-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.switch-label {
  font-size: 0.8125rem;
  font-weight: 700;
}
.sw {
  flex: none;
  width: 2.5rem;
  height: 1.5rem;
  border-radius: 99px;
  border: none;
  padding: 0.125rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background: #cbd5e1;
  cursor: pointer;
}
.sw.on {
  background: #7c3aed;
  justify-content: flex-end;
}
.sw span {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}
.custom-row.field {
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}
.custom-row.field input[type="number"] {
  width: 6rem;
}
</style>
